import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { BrandGroupModal, BrandModal, DeleteBrandModal } from "modules/BrandsDashboard/components";
import "modules/BrandsDashboard/components/BrandsDashboardTable/BrandsDashboardTable.scss";
import { BrandGroupBrandsTable } from "modules/BrandsDashboard/components/BrandsDashboardTable/components";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";

import type { ExpandedState } from "@tanstack/react-table";
import type { Brand, BrandGroup } from "models";

const columnHelper = createColumnHelper<BrandGroup>();

type BrandsDashboardTableProps = {
  selectedVinPrefixes: string[];
};

export const BrandsDashboardTable = ({ selectedVinPrefixes }: BrandsDashboardTableProps) => {
  const { t } = useTranslation();

  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const [selectedBrandGroup, setSelectedBrandGroup] = useState<BrandGroup | null>(null);
  const [newBrandGroupId, setNewBrandGroupId] = useState<number>();
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [brandToDelete, setBrandToDelete] = useState<number | null>(null);

  const { data: brandGroups, isLoading } = useBrandGroupsQuery();

  const filteredBrandGroups = selectedVinPrefixes.length
    ? brandGroups?.reduce<BrandGroup[]>((groups, group) => {
        const brands = group.brands?.filter(brand => selectedVinPrefixes.includes(brand.vin_prefix)) ?? [];
        return brands.length ? [...groups, { ...group, brands }] : groups;
      }, [])
    : brandGroups;

  useEffect(() => setExpandedRows(selectedVinPrefixes.length ? true : {}), [selectedVinPrefixes]);

  const handleToggleBrandGroup = (brandGroup: BrandGroup) => {
    if (!brandGroup.brands?.length) return;

    setExpandedRows(previousExpandedRows => {
      const currentExpandedRows = previousExpandedRows === true ? Object.fromEntries(filteredBrandGroups?.map(group => [group.id, true]) ?? []) : previousExpandedRows;
      return { ...currentExpandedRows, [brandGroup.id]: !currentExpandedRows[brandGroup.id] };
    });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "group",
        size: 250,
        header: () => <span className="no-wrap-text"> {t("v8_group", "Group")} </span>,
        cell: ({ row: { original, getIsExpanded } }) => (
          <div className="name-container">
            <div className={original.is_active ? "active" : "inactive"} />
            {!!original.brands?.length && (
              <div className="collapse-container">
                <Icon className={`caret ${getIsExpanded() ? "down" : "right"}`} />
              </div>
            )}
            <span className="no-wrap-text">{original.name}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "brands",
        size: 300,
        header: () => <span className="no-wrap-text"> {t("v8_brand_count", "Brand count")} </span>,
        cell: ({ row: { original } }) => <span className="no-wrap-text">{original.brands?.length ?? 0}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 60,
        cell: ({ row: { original: brandGroup } }) => (
          <div className="actions-container">
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.BrandGroups}>
              <div
                className="action-edit-button-container"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedBrandGroup(brandGroup);
                }}
              >
                <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" />
              </div>
            </Can>
            <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Brands}>
              <div
                className="action-add-button-container"
                onClick={e => {
                  e.stopPropagation();
                  setNewBrandGroupId(brandGroup.id);
                }}
              >
                <Icon className="plus action-button-icon-center" />
              </div>
            </Can>
          </div>
        )
      })
    ],
    [t]
  );

  return (
    <>
      <div className="BrandsDashboardTable">
        <ReactTable
          isFetching={isLoading}
          columns={columns}
          data={filteredBrandGroups}
          expanded={expandedRows}
          onExpandedChange={setExpandedRows}
          onRowClick={handleToggleBrandGroup}
          getRowId={brandGroup => `${brandGroup.id}`}
          renderExpandedComponent={row => <BrandGroupBrandsTable brands={row.original.brands} onEditBrand={setSelectedBrand} onDeleteBrand={setBrandToDelete} />}
        />
      </div>
      <BrandGroupModal isOpen={selectedBrandGroup !== null} brandGroup={selectedBrandGroup} onClose={() => setSelectedBrandGroup(null)} />
      <BrandModal isOpen={newBrandGroupId !== undefined} brand={null} brandGroupId={newBrandGroupId} onClose={() => setNewBrandGroupId(undefined)} />
      <BrandModal isOpen={selectedBrand !== null} brand={selectedBrand} onClose={() => setSelectedBrand(null)} />
      <DeleteBrandModal brandId={brandToDelete} onClose={() => setBrandToDelete(null)} />
    </>
  );
};
